import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { Check } from "@mui/icons-material";
import { throttle } from "lodash/function";
import { makeStyles } from "tss-react/mui";
import { Chip } from "@mui/material";

const useStyles = makeStyles()({
  inputRoot: {
    fontSize: 13,
    paddingTop: 6,
    paddingBottom: 7,
  },
});

function AsynchronousAutocomplete(props) {
  const { classes } = useStyles();
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [value, setValue] = React.useState(props.value !== undefined ? props.value : props.defaultValue);
  const [key, setKey] = React.useState(String(Math.random()));
  const [searchValue, setSearchValue] = React.useState("");
  const [inputValue, setInputValue] = React.useState(props.value?.label ? props.value.label : "");

  const [opened, setOpened] = React.useState(0);
  const [touched, setTouched] = React.useState(false);
  const labelsLoading = React.useRef(false);

  const loading = open && options.length === 0;

  const searchUrl = useMemo(() => {
    let url = props.async.url;

    if (url.includes("?")) {
      return url + `&per_page=${props.async.perPage ?? 250}`;
    } else {
      return url + `?per_page=${props.async.perPage ?? 250}`;
    }
  }, [props.async]);

  const labelUrl = useMemo(() => {
    let url = props.async.url;

    if (!url.includes("per_page")) {
      if (url.includes("?")) {
        return url + `&per_page=0`;
      } else {
        return url + `?per_page=0`;
      }
    }
    return url;
  }, [props.async]);

  const getLabels = React.useCallback(() => {
    if (!labelsLoading.current) {
      let data = [
        {
          field: props.primaryKey ?? "id",
          operator: "in",
          value: props.value.map((obj) => obj.value),
        },
      ];

      if (props.value.length > 0) {
        labelsLoading.current = true;

        axios.post(labelUrl, data).then((response) => {
          if (response.data.data.data.length >= 1 && response.data.data.data[0]["label"]) {
            setValue(
              response.data.data.data.map((obj) => ({
                value: obj.value,
                label: obj.label,
                label_extra: obj?.label_extra ?? null,
              }))
            );
          }
          labelsLoading.current = false;
        });
      }
    }
  }, [props.value, props.defaultValue]);

  const getLabel = React.useCallback(() => {
    if (!labelsLoading.current) {
      let data = [
        {
          field: props.primaryKey ?? "id",
          operator: "=",
          value: props.value?.value ?? props.defaultValue?.value,
        },
      ];

      if (props.value?.value ?? props.defaultValue?.value) {
        labelsLoading.current = true;

        axios.post(props.async.url, data).then((response) => {
          if (response.data.data.data.length >= 1 && response.data.data.data[0]["label"]) {
            setValue({ value: response.data.data.data[0].value, label: response.data.data.data[0].label });
          }
        });
      }
    }
  }, [props.value, props.defaultValue]);

  useEffect(() => {
    const value = props.value ?? props.defaultValue;

    if (value !== undefined) {
      if (props.multiple) {
        if (value?.[0]) {
          if (value[0].label == null) {
            getLabels();
            return;
          }
        }
      } else if (value?.label == null) {
        getLabel();
        return;
      }

      setValue(value);
    }
  }, [props.value, props.defaultValue]);

  /**
   * Find label when value does not have a label
   * This must be done when for example only a id is known on the first render
   */
  useEffect(() => {
    if (props.getLabel && value && !value.label && value.value > 0) {
      props.getLabel(props, value).then((label) => {
        setValue({ value: value.value, label: label });
        setInputValue(label);
      });
    }
  }, []);

  useEffect(() => {
    let active = true;

    if (!props.preloadOptions && !open) {
      if (props.setOptions) {
        props.setOptions([], setOptions);
      } else {
        setOptions([]);
      }
      if (props.onOptionsChange) {
        props.onOptionsChange(value, []);
      }
      return undefined;
    }

    fetch({ searchValue }, (results) => {
      if (active) {
        if (props.setOptions) {
          props.setOptions(results, setOptions);
        } else {
          setOptions(results);
        }

        if (props.onOptionsChange) {
          props.onOptionsChange(value, results);
        }
      }
    });

    return () => {
      active = false;
    };
  }, [loading, searchValue, setOptions, open]);

  const fetch = React.useMemo(
    () =>
      throttle(({ searchValue }, callback) => {
        let data = [
          {
            field: props.async.searchField,
            operator: "like",
            value: searchValue,
          },
        ];
        if (props.async.extraFilters) {
          data = data.concat(props.async.extraFilters);
        }
        axios.post(searchUrl, data).then((response) => {
          if (props.async.sort) {
            response.data.data.data = response.data.data.data.sort(props.async.sort);
          }
          if (response.data.data.data) {
            callback(response.data.data.data);
          } else if (response.data.data) {
            callback(response.data.data);
          }
        });
      }, 500),
    [searchUrl]
  );

  const getTags = () => {
    return value && Array.isArray(value)
      ? value.map((selectedOption, index) => {
          return (
            <Chip
              clickable={true}
              color={"primary"}
              key={selectedOption.value}
              label={
                selectedOption.label +
                (selectedOption?.label_extra !== undefined && selectedOption?.label_extra != null
                  ? " (" + selectedOption.label_extra + ")"
                  : "")
              }
              onDelete={() => {
                let newValue = [...value];
                newValue.splice(index, 1);
                setValue(newValue);
                setKey(String(Math.random()));
                props.onChange(null, newValue);
              }}
              size={"small"}
            />
          );
        })
      : null;
  };

  return (
    <>
      <Autocomplete
        blurOnSelect={!props.multiple}
        classes={{ inputRoot: classes.inputRoot }}
        disableClearable={props.multiple || props.disableClearable}
        disableCloseOnSelect={props.multiple}
        disabled={props.disabled}
        filterOptions={props.filterOptions ?? ((x) => x)}
        freeSolo={props.freeSolo}
        getOptionLabel={props.getOptionLabel ?? ((option) => option.label ?? "????")}
        inputValue={inputValue}
        isOptionEqualToValue={(option, value) => {
          return option.value === value.value;
        }}
        key={key}
        loading={loading}
        multiple={props.multiple}
        onChange={(e, value) => {
          setValue(value);
          props.onChange(e, value);
        }}
        onClose={() => {
          if (opened >= 1 || !touched) {
            setOpened(0);
            setOpen(false);
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (props.multiple) {
            if (reason === "clear" || reason === "input") {
              setInputValue(newInputValue);
              setSearchValue(newInputValue);
            }
          } else {
            setInputValue(newInputValue);
            if (reason === "reset") {
              setSearchValue("");
            } else {
              setSearchValue(newInputValue);
            }
          }
        }}
        onOpen={() => {
          setOpen(true);
        }}
        open={open}
        openOnFocus={true}
        options={options}
        renderInput={(params) => (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color={"inherit"} size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            onClick={(e) => {
              if (opened < 1 && touched) {
                e.preventDefault();
                e.stopPropagation();
              }
              setOpened((prev) => {
                return prev + 1;
              });
            }}
            onMouseDown={(e) => {
              if (opened <= 1 && touched) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            onTouchStart={(e) => {
              setTouched(true);
              if (opened <= 1) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            placeholder={props.placeholder}
            variant={"standard"}
          />
        )}
        renderOption={(optionProps, option, state) => {
          let render = [];
          if (props.getOptionLabel) {
            render.push(props.getOptionLabel(option));
          } else if (option.label) {
            render.push(option.label + (option?.label_extra !== undefined ? " (" + option.label_extra + ")" : ""));
          } else {
            render.push("????");
          }
          if (state.selected) {
            render.push(<Check key={"check element"} style={{ width: "0.7em", height: "0.7em", float: "right" }} />);
          }

          return (
            <li
              style={{ display: "flex", width: "100%", justifyContent: "space-between" }}
              {...optionProps}
              key={option.value}
            >
              {render}
            </li>
          );
        }}
        renderTags={
          !(props.multiple && !props.showTagsInInput)
            ? () => {
                return getTags();
              }
            : () => null
        }
        size={"small"}
        style={{ width: "100%", backgroundColor: "rgba(100,100,100,0.05)" }}
        value={value}
        {...props.autocompleteProps}
      />
      {props.multiple && !props.showTagsInInput ? getTags() : null}
    </>
  );
}

AsynchronousAutocomplete.propTypes = {
  async: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,

  multiple: PropTypes.bool,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  disabled: PropTypes.bool,
  filterOptions: PropTypes.func,
  disableClearable: PropTypes.bool,
  getLabel: PropTypes.func,
  setOptions: PropTypes.func,
  onOptionsChange: PropTypes.func,
  getOptionLabel: PropTypes.func,
  showTagsInInput: PropTypes.bool,
  primaryKey: PropTypes.string,
  preloadOptions: PropTypes.bool,
  freeSolo: PropTypes.bool,
  autocompleteProps: PropTypes.object,
};

AsynchronousAutocomplete.defaultProps = {
  multiple: false,
  placeholder: "",
  disableClearable: false,
  disabled: false,
  showTagsInInput: false,
  freeSolo: false,
  preloadOptions: false,
  autocompleteProps: {},
};

export default AsynchronousAutocomplete;
